import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import DialogLayout from "./DialogLayout";

type ConfirmDialogPropsType = {
  visible: boolean;
  title?: string;
  description?: React.ReactNode;
  alertIcon?: React.ReactNode;
  onClose: () => void;
  onSubmit: () => void | Promise<any>;
  closeBtnLabel?: string;
  submitBtnLabel?: string;
  dangerBtn?: boolean;
  width?: string | number;
  submitting?: boolean; // optional external control
};

const defaultTitle = "Delete this field?";
const defaultDescription =
  "The field test field and all of its contents will be deleted permanently.";

const ConfirmDialog = (props: ConfirmDialogPropsType) => {
  const { t } = useTranslation();
  const {
    visible,
    title = t(defaultTitle),
    description = t(defaultDescription),
    alertIcon,
    onClose,
    onSubmit,
    closeBtnLabel = t("Cancel"),
    submitBtnLabel = t("Confirm"),
    dangerBtn = false,
    width = 400,
    submitting,
  } = props;

  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = Boolean(submitting ?? internalLoading);

  const handleSubmit = async () => {
    if (isLoading) return;
    try {
      const ret = onSubmit?.();
      if (ret && typeof (ret as any).then === "function") {
        setInternalLoading(true);
        await ret;
      }
    } finally {
      setInternalLoading(false);
    }
  };

  const submitBtnClassName = dangerBtn
    ? "rounded-sm px-4 py-2 text-xs font-semibold shadow-xs text-white bg-red-600 hover:bg-red-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
    : "rounded-sm px-4 py-2 text-xs font-semibold shadow-xs text-white bg-blue-600 hover:bg-blue-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <DialogLayout show={visible}>
      <div style={{ width: width }}>
        <div className="flex relative h-12 items-center">
          <div className={"pl-2"}></div>
          <div className={"absolute right-2"}>
            <XMarkIcon onClick={onClose} className={"w-5 cursor-pointer hover:text-blue-600"} />
          </div>
        </div>
        {alertIcon && <div className="flex justify-center pb-3">{alertIcon}</div>}
        <div className="text-center px-4 pb-3 text-xl font-bold">{title}</div>
        <div className="text-center px-4 pb-3 text-base text-gray-500">{description}</div>
        <div className="w-full">
          <div className={"flex space-x-2 justify-center w-full p-3"}>
            <button
              onClick={onClose}
              type="button"
              className="rounded-sm px-4 py-2 text-xs font-semibold shadow-xs border-1 border-gray-300 border-solid hover:bg-blue-500 hover:text-white focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              disabled={isLoading}
            >
              {closeBtnLabel}
            </button>
            <button
              onClick={handleSubmit}
              type="button"
              className={submitBtnClassName}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 border-2 border-white/60 border-t-white rounded-full animate-spin"></span>
                  <span>{t("Processing...")}</span>
                </span>
              ) : (
                submitBtnLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ConfirmDialog;
